<template>
  <div>
    <v-row>
      <page-title value="Nursery Diary"></page-title>
      <v-spacer></v-spacer>
      <!-- 印刷 -->
      <v-btn color="black" class="mx-2" variant="outlined">
        <v-icon start>mdi-printer</v-icon>
        Print
      </v-btn>
      <template v-if="reportExists">
        <!-- 下書き保存 -->
        <v-btn
          @click="updateReport('draft')"
          color="primary"
          class="mx-2"
          variant="outlined"
          >Save Draft</v-btn
        >
        <!-- 申請 -->
        <v-btn
          @click="updateReport('applied')"
          color="primary"
          class="ml-2"
          variant="flat"
          >Submit</v-btn
        >
      </template>
      <template v-else>
        <!-- 下書き保存 -->
        <v-btn
          @click="createReport('draft')"
          color="primary"
          class="mx-2"
          variant="outlined"
          >Save Draft</v-btn
        >
        <!-- 申請 -->
        <v-btn
          @click="createReport('applied')"
          color="primary"
          class="ml-2"
          variant="flat"
          >Submit</v-btn
        >
      </template>
    </v-row>
    <v-row>
      <date-pager v-model="date" class="pt-4"></date-pager>
    </v-row>

    <div>{{ classDiaries }}</div>

    <v-form ref="form">
      <v-container fluid class="pt-10">
        <v-row>
          <v-col cols="6">
            <div class="pb-2">Today's Objective</div>
            <v-textarea v-model="aim" variant="outlined"></v-textarea>
          </v-col>
          <v-col cols="6">
            <div class="pb-2">Main Activities</div>
            <v-text-field
              v-model="activityAm"
              variant="outlined"
              label="Morning"
            ></v-text-field>
            <v-text-field
              v-model="activityPm"
              variant="outlined"
              label="Afternoon"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <div class="pb-2">Free Notes</div>
            <v-textarea v-model="comment" variant="outlined"></v-textarea>
          </v-col>
          <v-col cols="6">
            <div class="pb-2">School Events & Visitors</div>
            <v-textarea v-model="event" variant="outlined"></v-textarea>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <div class="pb-2">Absentees (Reason)</div>
            <v-textarea v-model="absentee" variant="outlined"></v-textarea>
          </v-col>
          <v-col cols="6"> </v-col>
        </v-row>
      </v-container>
    </v-form>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import PageTitle from "@/components/atoms/PageTitle.vue";
import DatePager from "@/components/atoms/DatePager.vue";
import { nurseryReportModule } from "@/store/dataModules/nurseryReportModule";
import { nurseryReportEditModule } from "@/store/dataModules/nurseryReportEditModule";
import LocalDataService from "@/service/LocalDataService";
import Swal from "sweetalert2";
import dayjs from "dayjs";

export default defineComponent({
  name: "NurseryReport",

  components: {
    PageTitle,
    DatePager,
  },

  computed: {
    classDiaries() {
      if (nurseryReportModule.report) {
        return nurseryReportModule.report.classDiaries;
      }
      return [];
    },
    aim: {
      get() {
        return nurseryReportEditModule.aim;
      },
      set(value: string) {
        nurseryReportEditModule.setAim(value);
      },
    },
    date: {
      get() {
        return nurseryReportEditModule.date;
      },
      set(value: string) {
        nurseryReportEditModule.setDate(value);
        this.getReport();
      },
    },
    activityAm: {
      get() {
        return nurseryReportEditModule.activityAm;
      },
      set(value: string) {
        nurseryReportEditModule.setActivityAm(value);
      },
    },
    activityPm: {
      get() {
        return nurseryReportEditModule.activityPm;
      },
      set(value: string) {
        nurseryReportEditModule.setActivityPm(value);
      },
    },
    comment: {
      get() {
        return nurseryReportEditModule.comment;
      },
      set(value: string) {
        nurseryReportEditModule.setComment(value);
      },
    },
    event: {
      get() {
        return nurseryReportEditModule.event;
      },
      set(value: string) {
        nurseryReportEditModule.setEvent(value);
      },
    },
    absentee: {
      get() {
        return nurseryReportEditModule.absentee;
      },
      set(value: string) {
        nurseryReportEditModule.setAbsentee(value);
      },
    },
    reportExists(): boolean {
      return nurseryReportModule.report?.schoolDiaryId ? true : false;
    },
  },

  methods: {
    async createReport(status?: "draft" | "applied") {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        if (status) {
          nurseryReportEditModule.setStatus(status);
        }
        const editReponse = await nurseryReportEditModule.createReport({
          nurseryId: nurseryId,
        });
        if (editReponse) {
          Swal.fire({ title: "Diary created", icon: "success" });
          this.getReport();
        }
      }
    },
    async updateReport(status?: "draft" | "applied") {
      const nurseryId = LocalDataService.getNurseryId();
      const schoolDiaryId = nurseryReportModule.report?.schoolDiaryId;
      if (nurseryId && schoolDiaryId) {
        if (status) {
          nurseryReportEditModule.setStatus(status);
        }
        const editReponse = await nurseryReportEditModule.updateReport({
          schoolDiaryId: schoolDiaryId,
          nurseryId: nurseryId,
        });
        if (editReponse) {
          Swal.fire({ title: "Diary updated", icon: "success" });
          this.getReport();
        }
      }
    },
    async getReport() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const date = nurseryReportEditModule.date;
        let input;
        if (date) {
          input = {
            nurseryId: nurseryId,
            date: date,
          };
        } else {
          input = {
            nurseryId: nurseryId,
          };
        }
        (this.$refs.form as any).reset();
        const response = await nurseryReportModule.getNurseryReport(input);
        nurseryReportEditModule.setAim(response.aim);
        nurseryReportEditModule.setActivityAm(response.activityAm);
        nurseryReportEditModule.setActivityPm(response.activityPm);
        nurseryReportEditModule.setComment(response.comment);
        nurseryReportEditModule.setEvent(response.event);
        nurseryReportEditModule.setAbsentee(response.absentee);
      }
    },
    setDate(date: string) {
      nurseryReportEditModule.setDate(date);
      this.getReport();
    },
  },

  mounted() {
    const today = dayjs().format("YYYY-MM-DD");
    this.setDate(today);
  },
});
</script>
